import React, { useEffect, useState } from "react";
import { useInfiniteHits } from "react-instantsearch";
import { Listing } from "./listing";
import LoadMoreButton from "./buttons/load-more-button";

// Custom hit component for individual listings
const Hit = ({ hit, favorites, onAddToFavorites }) => {
  // Extract image URL from the hit object - updated for new Algolia structure
  const getImageUrl = (hit) => {
    // Check for new img array field first
    if (hit.img && Array.isArray(hit.img) && hit.img.length > 0) {
      return hit.img[0]; // Return first image from array
    }
    // Fallback to old structure for backward compatibility
    if (hit.cover_photo && hit.cover_photo.url) {
      return hit.cover_photo.url;
    }
    if (hit.image_url) {
      return hit.image_url;
    }
    return null;
  };

  // Create images array - updated for new Algolia structure
  const getImages = (hit) => {
    // Check for new img array field first
    if (hit.img && Array.isArray(hit.img)) {
      return hit.img; // Return the full array
    }
    // Fallback to old structure for backward compatibility
    const imageUrl = getImageUrl(hit);
    return imageUrl ? [imageUrl] : [];
  };

  const imageUrl = getImageUrl(hit);
  const allImages = getImages(hit);

  return (
    <Listing
      title={hit.title}
      price={Number(hit.price).toLocaleString("en-US", {
        style: "currency",
        currency: "JPY",
      })}
      size={hit.size || "-"}
      pictureUrl={imageUrl}
      allImages={allImages}
      img={hit.img} // Pass the new img array
      url={hit.url}
      objectID={hit.objectID}
      createdAt={
        hit.created_at
          ? new Date(
              Number(hit.created_at) *
                (String(hit.created_at).length === 10 ? 1000 : 1)
            ).toISOString()
          : ""
      } // Convert timestamp to an ISO datetime string (supports seconds or ms)
      shop={hit.shop} // Pass the shop field
      onAddToFavorites={() => onAddToFavorites(hit)}
      favorited={
        Array.isArray(favorites) &&
        favorites.some((favorite) => favorite.id === hit.objectID)
      }
    />
  );
};

const toTimestamp = (value) => {
  if (!value) {
    return 0;
  }

  const numericValue =
    typeof value === "number" ? value : Number.parseInt(value, 10);

  if (!Number.isFinite(numericValue)) {
    return 0;
  }

  const isSeconds = String(value).length === 10;
  return isSeconds ? numericValue * 1000 : numericValue;
};

// Sort newest-first by listing creation time
const byNewest = (firstHit, secondHit) =>
  toTimestamp(
    secondHit.created_at || secondHit.createdAt || secondHit.created_at_ms
  ) -
  toTimestamp(
    firstHit.created_at || firstHit.createdAt || firstHit.created_at_ms
  );

// Custom Hits component with infinite loading
const AlgoliaHits = ({ favorites, onAddToFavorites }) => {
  const { hits, isLastPage, showMore } = useInfiniteHits();

  // We keep our own accumulated list instead of re-sorting `hits` on every
  // render. Re-sorting the whole accumulated list reshuffled already-rendered
  // listings on each "Load more", which made the page jump, reloaded images,
  // and buried the newly fetched listings somewhere in the middle. Here each
  // newly fetched page is sorted on its own and appended to the bottom, so
  // existing listings never move and new ones always appear at the end.
  const [displayedHits, setDisplayedHits] = useState([]);

  useEffect(() => {
    setDisplayedHits((previous) => {
      const currentIds = new Set(hits.map((hit) => hit.objectID));
      const stillPresent = previous.filter((hit) => currentIds.has(hit.objectID));

      // A new search / changed filters replaces the result set rather than
      // extending it: some previously shown hits are gone. Rebuild from scratch.
      const isReset =
        previous.length === 0 || stillPresent.length !== previous.length;
      if (isReset) {
        return [...hits].sort(byNewest);
      }

      // Otherwise this is a "Load more": append only the newly fetched hits,
      // sorted among themselves, keeping everything already shown in place.
      const seen = new Set(previous.map((hit) => hit.objectID));
      const incoming = hits
        .filter((hit) => !seen.has(hit.objectID))
        .sort(byNewest);

      return incoming.length > 0 ? [...previous, ...incoming] : previous;
    });
  }, [hits]);

  return (
    <div className="listings" id="listings">
      <div className="listings__grid">
        {displayedHits.map((hit) => (
          <Hit
            key={hit.objectID}
            hit={hit}
            favorites={favorites}
            onAddToFavorites={onAddToFavorites}
          />
        ))}
      </div>

      {!isLastPage && (
        <LoadMoreButton loadMore={showMore} buttonText="Load more listings" />
      )}

      <hr className="listings__line" />
    </div>
  );
};

export default AlgoliaHits;
